import { DateTime } from "luxon";

import type { DateTimeLike } from './date-time';
import { MARKET_TZ, type MarketInstant, toMarketTime } from './market-clock';

/*
 * The webinar rules every surface shares. When a session starts: webinarStartTime, which reads the CMS value as New
 * York wall-clock time, so every reader of Webinar.webinarDate goes through it (or through webinarPhase and
 * webinarStartLabels, which do). Where a session stands: webinarPhase. The dashboard, the featured replay, the listing
 * tabs and the preview badge each had their own phase rule (start > now − 1h, start ≤ now − 1h, start ≥ now, start of
 * day ≥ local start of day), so a live session was 'Past' on one card and 'Upcoming' on the next.
 * Pure (no Vue): apps/web reads it through the shared market clock, so the phase moves on in an open tab.
 */

/** 'upcoming' before the start, 'live' from the start until the session's length has run, then 'past'. */
export type WebinarPhase = 'upcoming' | 'live' | 'past';

/** The CMS carries no session length; sessions run about an hour, and the dashboard always kept one listed that long. */
export const WEBINAR_DEFAULT_DURATION_MIN: number = 60;

// A date-time (group 1) followed by the content service's Unspecified marker: 'Z', or an offset of zero ('+00:00',
// '-00:00', '+0000', '+00'). A real offset such as '-04:00' or '+05:30' does not match.
const UNSPECIFIED_MARKER: RegExp = /^(.+T.+?)(?:Z|[+-]00(?::?00)?)$/i;

// Each start string's instant, read once. The listings, the dashboard and the bell re-read every session's start on
// the shared clock's 30 s tick, and a parse on New York's clock costs several times one at a fixed offset. The instant
// is a pure function of the string (it never depends on the viewer's zone), so an entry never goes stale; the cap only
// bounds memory, as the listing-day cache in apps/web's date-range filter does.
const START_CACHE_LIMIT: number = 5_000;
const startByText: Map< string, DateTime | null > = new Map< string, DateTime | null >();

function readStartText( text: string ): DateTime | null {
    const trimmed: string = text.trim();
    const wallClock: string = UNSPECIFIED_MARKER.exec( trimmed )?.[ 1 ] ?? trimmed;
    // A string without a zone is read as New York wall-clock time; one that still names a real offset keeps the
    // instant it names, and is only shown on New York's clock
    const startsAt: DateTime = DateTime.fromISO( wallClock, { zone: MARKET_TZ } );
    return startsAt.isValid ? startsAt : null;
}

/**
 * The session's start: an instant, on New York's clock, or null when there is none to read.
 *
 * Editors type a webinar's start into Contentful as New York wall-clock time: noon for a session that goes live at
 * 12:00 PM ET. The content service keeps only those digits (an Unspecified .NET DateTime: it drops the offset Contentful
 * stores) and serializes them as HotChocolate did, with a literal 'Z': `"publish_dt":"2026-07-22T13:05:00.000Z"`, pinned
 * by services/content's DateTimeWireFormatTests and golden/*.json. The webinar sync maps publish_dt to webinarDate. So
 * the digits are New York time and the 'Z' names no zone: that value is a 1:05 PM ET session, 17:05 UTC. Read as ISO
 * says ('Z' is UTC), every session showed, went live and aired four hours early (five in winter), and editors took to
 * typing UTC times to make up for it.
 *
 * The rule:
 * - A string's trailing 'Z' or zero offset ('+00:00', '-00:00', '+0000', '+00') is that Unspecified marker. It is
 *   dropped, and the digits are read on New York's clock, EDT or EST as that date keeps it. A string with no zone reads
 *   the same way, and a date alone ('2026-09-29') is midnight ET that day. A time the spring-forward skips (2:30 AM that
 *   Sunday) moves on an hour; one the fall-back repeats is the first, EDT, one.
 * - A string with a real, non-zero offset ('2026-09-29T14:00:00-04:00') names an instant, and that instant is kept. The
 *   wire never sends one today; if the service ever passes on the offset Contentful stores, it is read correctly.
 * - A Date, a DateTime or epoch milliseconds is an instant already, usually one this function returned (the Webinars
 *   store sorts on it and hands it to webinarPhase), so it is taken as it is and never shifted a second time.
 * - Anything else is no usable start, and null, never "now": undefined, null, '', a blank string, a string that is not
 *   ISO 8601, and an invalid date. (normalizeDateTime reads undefined as the current instant.)
 */
export function webinarStartTime( start: DateTimeLike | null ): DateTime | null {
    if ( null == start ) {
        return null;
    }

    if ( 'string' === typeof start ) {
        const cached: DateTime | null | undefined = startByText.get( start );
        if ( undefined !== cached ) {
            return cached;
        }

        if ( startByText.size >= START_CACHE_LIMIT ) {
            startByText.clear();
        }

        const startsAt: DateTime | null = readStartText( start );
        startByText.set( start, startsAt );
        return startsAt;
    }

    const startsAt: DateTime = toMarketTime( start );
    return startsAt.isValid ? startsAt : null;
}

/**
 * Sessions in start order, soonest first (swap the arguments for newest first), by webinarStartTime. A session with no
 * usable start sorts as the oldest, before every dated one: webinarPhase counts it past.
 */
export function compareWebinarStarts( a: DateTimeLike | null, b: DateTimeLike | null ): number {
    const left: number | undefined = webinarStartTime( a )?.toMillis();
    const right: number | undefined = webinarStartTime( b )?.toMillis();
    if ( undefined === left || undefined === right ) {
        return ( undefined === left ? 0 : 1 ) - ( undefined === right ? 0 : 1 );
    }

    return left - right;
}

/**
 * Where the session stands at `now`, its start read by webinarStartTime (the digits are New York time). A session with
 * no usable start counts as past: legacy content without a date has, in practice, already aired.
 */
export function webinarPhase( start: DateTimeLike | null, now: MarketInstant, durationMin: number = WEBINAR_DEFAULT_DURATION_MIN ): WebinarPhase {
    const startsAt: DateTime | null = webinarStartTime( start );
    if ( !startsAt ) {
        return 'past';
    }

    const nowMillis: number = toMarketTime( now ).toMillis();
    const startMillis: number = startsAt.toMillis();
    if ( nowMillis < startMillis ) {
        return 'upcoming';
    }

    return nowMillis < startMillis + Math.max( 0, durationMin ) * 60_000 ? 'live' : 'past';
}

export interface WebinarStartLabels {
    /** The start instant in UTC ISO, for a <time datetime>: '2026-09-29T18:00:00.000Z' for a 2:00 PM ET session. */
    iso: string;
    /** The start on New York's clock, as the editor typed it: 'Tue, Sep 29, 2:00 PM ET'. */
    market: string;
    /** The same instant on the viewer's clock ('11:00 AM PDT', weekday added when the day differs); '' on ET. */
    local: string;
    /** The session's New York calendar day, 'September 29, 2026', for a session that has aired. */
    day: string;
}

const LABEL_LOCALE: string = 'en-US';

/**
 * How a session's start reads, by webinarStartTime. Webinars are scheduled on New York time, so that is the time shown;
 * a member whose zone keeps a different offset at that instant also gets their own. The viewer's zone is Luxon's
 * default zone (the browser's, unless Settings.defaultZone says otherwise).
 */
export function webinarStartLabels( start: DateTimeLike | null ): WebinarStartLabels | null {
    const startsAt: DateTime | null = webinarStartTime( start );
    if ( !startsAt ) {
        return null;
    }

    const market: DateTime = startsAt.setZone( MARKET_TZ ).setLocale( LABEL_LOCALE );
    const local: DateTime = startsAt.toLocal().setLocale( LABEL_LOCALE );
    const sameDay: boolean = market.toISODate() === local.toISODate();

    return {
        iso: startsAt.toUTC().toISO() ?? '',
        market: `${ market.toFormat( 'ccc, LLL d, h:mm a' ) } ET`,
        local: local.offset === market.offset ? '' : local.toFormat( sameDay ? 'h:mm a ZZZZ' : 'ccc h:mm a ZZZZ' ),
        day: market.toFormat( 'LLLL d, yyyy' )
    };
}

/** A video's length for a label: '58 min', '1 hr 5 min'. '' for a length that is not a positive number. */
export function webinarLengthLabel( seconds: number | null | undefined ): string {
    if ( null == seconds || !Number.isFinite( seconds ) || seconds <= 0 ) {
        return '';
    }

    const minutes: number = Math.max( 1, Math.round( seconds / 60 ) );
    const hours: number = Math.floor( minutes / 60 );
    const rest: number = minutes % 60;
    if ( 0 === hours ) {
        return `${ minutes } min`;
    }

    return rest > 0 ? `${ hours } hr ${ rest } min` : `${ hours } hr`;
}

/**
 * The registration or join link to open, or null. Only an http(s) URL is used: the value is CMS text bound to an
 * href, and a scheme-less 'zoom.us/…' is common enough to read as https.
 */
export function webinarRegistrationUrl( value: string | null | undefined ): string | null {
    const raw: string = ( value ?? '' ).trim();
    if ( !raw ) {
        return null;
    }

    let url: URL;
    try {
        url = new URL( /^[a-z][a-z\d+.-]*:/i.test( raw ) ? raw : `https://${ raw.replace( /^\/+/, '' ) }` );
    } catch {
        return null;
    }

    return 'https:' === url.protocol || 'http:' === url.protocol ? url.toString() : null;
}
